import { Avatar } from "@/components/ui/avatar"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"

interface UserAvatarProps {
  className?: string
  displayName?: string
  avatarUrl?: string
  profilePicture?: string
  showInitials?: boolean
  alt?: string
}

/**
 * Reusable UserAvatar component that handles avatar priority:
 * 1. Custom profile picture (if uploaded via settings)
 * 2. WordPress Gravatar
 * 3. User initials fallback
 */
export function UserAvatar({ 
  className = "h-8 w-8", 
  displayName,
  avatarUrl,
  profilePicture,
  showInitials = true,
  alt 
}: UserAvatarProps) {
  const { user } = useWordPressAuth()
  
  // Use props or fallback to current user
  const finalDisplayName = displayName || user?.displayName || 'User'
  const finalAvatarUrl = avatarUrl || user?.avatarUrl
  const finalProfilePicture = profilePicture || user?.profilePicture
  const finalAlt = alt || finalDisplayName

  // Avatar priority: custom profile picture > gravatar > initials
  const getAvatarSrc = () => {
    const isDevMode = (window as any).WP_COCKPIT?.devMode || false
    
    if (isDevMode) {
      console.log('[UserAvatar] Priority check:', {
        finalProfilePicture,
        finalAvatarUrl,
        currentUser: user,
        propsProfilePicture: profilePicture,
        userProfilePicture: user?.profilePicture
      })
    }
    
    if (finalProfilePicture && finalProfilePicture.trim()) return finalProfilePicture
    if (finalAvatarUrl && finalAvatarUrl.trim()) return finalAvatarUrl
    return null
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const avatarSrc = getAvatarSrc()

  return (
    <Avatar className={`flex-shrink-0 ${className}`}>
      {avatarSrc ? (
        <img 
          src={avatarSrc} 
          alt={finalAlt}
          className="h-full w-full object-cover rounded-full" 
          onError={(e) => {
            const isDevMode = (window as any).WP_COCKPIT?.devMode || false
            if (isDevMode) console.log('[UserAvatar] Image failed to load:', avatarSrc)
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : showInitials ? (
        <div className="h-full w-full bg-primary/10 flex items-center justify-center rounded-full">
          <span className="text-xs font-medium text-primary">
            {getInitials(finalDisplayName)}
          </span>
        </div>
      ) : null}
    </Avatar>
  )
}